/**
 * AchievementsGrid — 9-slot eneatype unlock board.
 *
 * Each slot has 3 visual states:
 *   - hard unlocked (bright + accent + score + date)
 *   - padrao unlocked (dim + "Padrão concluído" tag)
 *   - locked (muted, lock icon)
 *
 * Used both on the report page and on the setup page so the user always sees
 * progress towards "9/9 hard" (the leaderboard-zerou threshold).
 */
import { Lock, CheckCircle, Trophy } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { TYPE_ACCENT } from "@/features/decodificar/content";
import { ENEATYPE_NAMES, type AchievementRow } from "../types";
import { cn } from "@/lib/utils";

export interface AchievementsGridProps {
  achievements: AchievementRow[];
  isLoading?: boolean;
  className?: string;
}

function pickByEneatype(rows: AchievementRow[], en: number) {
  const hard = rows.find((r) => r.eneatype === en && r.difficulty === "hard");
  const padrao = rows.find((r) => r.eneatype === en && r.difficulty === "padrao");
  return { hard, padrao };
}

export function AchievementsGrid({
  achievements,
  isLoading,
  className,
}: AchievementsGridProps) {
  const hardCount = achievements.filter((a) => a.difficulty === "hard").length;

  return (
    <Card className={cn("p-4 bg-card/80 border-border/50", className)} data-testid="achievements-grid">
      <header className="mb-3">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Trophy weight="duotone" className="w-4 h-4 text-amber-400" />
          Conquistas — {hardCount} / 9 fechados no Difícil
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Feche um Difícil de cada personalidade pra zerar o Simulador.
        </p>
      </header>
      <Progress
        value={(hardCount / 9) * 100}
        className="h-1.5 mb-4"
        aria-label={`Progresso do Simulador: ${hardCount} de 9 personalidades fechadas no Difícil`}
      />

      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-9 gap-2">
        {Array.from({ length: 9 }, (_, i) => i + 1).map((en) => {
          const { hard, padrao } = pickByEneatype(achievements, en);
          const accent = TYPE_ACCENT[en as keyof typeof TYPE_ACCENT];

          if (hard) {
            return (
              <div
                key={en}
                className="rounded-xl border p-3 text-center transition"
                style={{
                  background: `${accent}18`,
                  borderColor: `${accent}55`,
                }}
                data-testid={`achievement-slot-${en}-hard`}
              >
                <div
                  className="mx-auto mb-1 flex h-8 w-8 items-center justify-center rounded-full"
                  style={{ background: `${accent}33`, color: accent }}
                >
                  <CheckCircle weight="fill" className="w-5 h-5" />
                </div>
                <div className="text-[11px] font-semibold text-foreground">
                  T{en}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {ENEATYPE_NAMES[en]}
                </div>
                <div className="mt-1 text-[10px] font-semibold" style={{ color: accent }}>
                  {hard.final_score}/100
                </div>
              </div>
            );
          }
          if (padrao) {
            return (
              <div
                key={en}
                className="rounded-xl border border-border/40 bg-secondary/40 p-3 text-center"
                data-testid={`achievement-slot-${en}-padrao`}
              >
                <div className="mx-auto mb-1 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
                  <CheckCircle weight="duotone" className="w-5 h-5" />
                </div>
                <div className="text-[11px] font-semibold text-foreground/80">
                  T{en}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {ENEATYPE_NAMES[en]}
                </div>
                <div className="mt-1 text-[10px] text-emerald-400/90">
                  Padrão concluído
                </div>
              </div>
            );
          }
          return (
            <div
              key={en}
              className="rounded-xl border border-border/30 bg-background/40 p-3 text-center opacity-60"
              data-testid={`achievement-slot-${en}-locked`}
            >
              <div className="mx-auto mb-1 flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <Lock weight="duotone" className="w-4 h-4" />
              </div>
              <div className="text-[11px] font-semibold text-muted-foreground">
                T{en}
              </div>
              <div className="text-[10px] text-muted-foreground/70 truncate">
                {ENEATYPE_NAMES[en]}
              </div>
            </div>
          );
        })}
      </div>

      {isLoading ? (
        <p className="mt-3 text-[11px] text-muted-foreground">Atualizando…</p>
      ) : null}
    </Card>
  );
}
